import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { addItemInArray, addItemsInArray, deleteItemFromArray, updateItemInArray, updateItemsInArray } from '../utils/data';
import { StorageEngine } from './storage-engine';

export class Store {
  private data: {[key: string]: BehaviorSubject<any>} = {};

  constructor(private engine?: StorageEngine) {
  }

  get(key: string, defaultValue?: any): Observable<any> {
    return this.getSubject(key).pipe(
      map(item => item === undefined ? defaultValue : item),
      filter(item => item !== undefined)
    );
  }

  value(key: string, defaultValue?: any): any {
    return this.getSubject(key).value === undefined ? defaultValue : this.getSubject(key).value;
  }

  save(key: string, value: any): void {
    this.getSubject(key).next(value);
    if (this.engine) {
      this.engine.save(key, value);
    }
  }

  appendItem(key: string, value: any, checkFunction?: (item: any) => boolean): void {
    this.save(key, addItemInArray(this.value(key) || [], value, true, checkFunction));
  }

  appendItems(key: string, value: any[], checkFunction?: (item: any) => boolean): void {
    this.save(key, addItemsInArray(this.value(key) || [], value, true));
  }

  prependItem(key: string, value: any, checkFunction?: (item: any) => boolean): void {
    this.save(key, addItemInArray(this.value(key) || [], value, false, checkFunction));
  }

  prependItems(key: string, value: any[], checkFunction?: (item: any) => boolean): void {
    this.save(key, addItemsInArray(this.value(key) || [], value, false, checkFunction));
  }

  updateItem(key: string, value: any, force?: boolean, checkFunction?: (item: any) => boolean): void {
    this.save(key, updateItemInArray(this.value(key) || [], value, force, checkFunction));
  }

  updateItems(key: string, value: any, force?: boolean, checkFunction?: (item: any) => boolean): void {
    this.save(key, updateItemsInArray(this.value(key) || [], value, force, checkFunction));
  }

  deleteItem(key: string, value: any): void {
    this.save(key, deleteItemFromArray(this.value(key) || [], value));
  }

  dispose(): void {
    for (const key of Object.keys(this.data)) {
      this.data[key].complete();
      delete this.data[key];
    }
  }

  clearStorageData(): void {
    if (this.engine) {
      this.engine.clear();
    }
  }

  getEngine(): StorageEngine {
    return this.engine;
  }

  private getSubject(key: string): BehaviorSubject<any> {
    if (!this.data[key]) {
      this.data[key] = new BehaviorSubject<any>(this.engine ? this.engine.get(key) : undefined);
    }
    return this.data[key];
  }
}
